import React from 'react';
import { Badge } from '@/components/ui/badge';
import { ExternalLink } from 'lucide-react';

// --- Shared formatters ---

export const formatCurrency = (value) => {
  if (!value && value !== 0) return '—';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value) + ' ₸';
};

export const formatPercent = (value) => {
  if (!value && value !== 0) return '—';
  return value.toFixed(1) + '%';
};

export const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU');
};

// --- Shared components ---

export const HealthBadge = ({ status }) => {
  const config = {
    green: { label: 'В графике', className: 'bg-green-100 text-green-800' },
    yellow: { label: 'Риск', className: 'bg-yellow-100 text-yellow-800' },
    red: { label: 'Критично', className: 'bg-red-100 text-red-800' },
  };
  const { label, className } = config[status] || config.green;
  return <Badge className={className}>{label}</Badge>;
};

// --- Currency cell renderer helper ---
const currencyCell = (value) => value ? formatCurrency(value) : '—';
const percentCell = (value) => value != null ? formatPercent(value) : '—';
const dateCell = (value) => formatDate(value);

/**
 * OVERVIEW_COLUMNS — 37 column definitions for ProjectsOverviewPage
 *
 * Column shape:
 *   key: string — field name in project object
 *   label: string — header text
 *   block: string — semantic group
 *   width: number — default width in px
 *   filterType: 'text' | 'select' | 'currency' | 'percent' | 'date' | 'none'
 *   align: 'left' | 'right' | 'center'
 *   finance: boolean — true = hidden for non-finance roles
 *   options?: string[] — for select filters
 *   optionLabels?: Record<string, string> — display labels for select options
 *   render?: (value, row) => ReactNode — custom cell renderer
 */
export const OVERVIEW_COLUMNS = [
  // === ОСНОВНЫЕ (6) ===
  {
    key: 'name',
    label: 'Название',
    block: 'main',
    width: 280,
    filterType: 'text',
    align: 'left',
    finance: false,
    render: (value, row) => {
      const isClosed = row?.pcc_status === 'Closed';
      return (
        <div
          className={`font-medium truncate ${isClosed ? 'text-gray-400 line-through' : 'text-gray-900'}`}
          title={value}
        >
          {value || '—'}
        </div>
      );
    },
  },
  {
    key: 'health_status',
    label: 'Статус',
    block: 'main',
    width: 90,
    filterType: 'select',
    align: 'left',
    finance: false,
    options: ['green', 'yellow', 'red'],
    optionLabels: { green: 'В графике', yellow: 'Риск', red: 'Критично' },
    render: (value) => <HealthBadge status={value} />,
  },
  {
    key: 'expected_end_date',
    label: 'Дедлайн',
    block: 'main',
    width: 100,
    filterType: 'date',
    align: 'left',
    finance: false,
    render: (value) => (
      <span className="text-gray-600">{dateCell(value)}</span>
    ),
  },
  {
    key: 'manager',
    label: 'Менеджер',
    block: 'main',
    width: 160,
    filterType: 'text',
    align: 'left',
    finance: false,
    render: (value, row) => {
      const name = value || row.responsible_name || '—';
      return (
        <span className="text-gray-600 truncate block" title={name}>
          {name}
        </span>
      );
    },
  },
  {
    key: 'manager_bitrix_id',
    label: 'Bitrix User ID',
    block: 'main',
    width: 100,
    filterType: 'text',
    align: 'left',
    finance: false,
    render: (value) => <span className="text-gray-600">{value || '—'}</span>,
  },
  {
    key: 'bitrix_task_url',
    label: 'Bitrix',
    block: 'main',
    width: 50,
    filterType: 'none',
    align: 'center',
    finance: false,
    render: (value) => value ? (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-blue-500 hover:text-blue-700 inline-flex justify-center"
      >
        <ExternalLink className="w-4 h-4" />
      </a>
    ) : null,
  },

  // === ПАСПОРТ (6) ===
  {
    key: 'bitrix_task_id',
    label: 'Bitrix ID',
    block: 'passport',
    width: 80,
    filterType: 'text',
    align: 'left',
    finance: false,
    render: (value) => <span className="text-gray-600">{value || '—'}</span>,
  },
  {
    key: 'year',
    label: 'Год',
    block: 'passport',
    width: 60,
    filterType: 'text',
    align: 'left',
    finance: false,
    render: (value) => <span className="text-gray-600">{value || '—'}</span>,
  },
  {
    key: 'client_name',
    label: 'Заказчик',
    block: 'passport',
    width: 150,
    filterType: 'text',
    align: 'left',
    finance: false,
    render: (value) => (
      <span className="text-gray-600 truncate block" title={value}>
        {value || '—'}
      </span>
    ),
  },
  {
    key: 'vendor_name',
    label: 'Вендор',
    block: 'passport',
    width: 160,
    filterType: 'text',
    align: 'left',
    finance: false,
    render: (value) => <span className="text-gray-600">{value || '—'}</span>,
  },
  {
    key: 'project_status',
    label: 'Статус проекта',
    block: 'passport',
    width: 130,
    filterType: 'select',
    align: 'left',
    finance: false,
    options: [
      'Разработка КП', 'КП передано Заказчику',
      'Разработка ТС', 'ТС передана Заказчику',
      'Конкурс объявлен', 'Подписание договора',
      'Исполнение договора', 'Закрытие договора',
    ],
    render: (value) => value ? (
      <Badge variant="outline" className="text-xs">{value}</Badge>
    ) : '—',
  },
  {
    key: 'client_budget_with_vat',
    label: 'Бюджет заказчика',
    block: 'passport',
    width: 150,
    filterType: 'currency',
    align: 'right',
    finance: false,
    render: currencyCell,
  },

  // === ОЦЕНКА (3) ===
  {
    key: 'kp_amount_with_vat',
    label: 'Сумма КП с НДС',
    block: 'assessment',
    width: 140,
    filterType: 'currency',
    align: 'right',
    finance: false,
    render: currencyCell,
  },
  {
    key: 'gpl_project_with_vat',
    label: 'GPL с НДС',
    block: 'assessment',
    width: 140,
    filterType: 'currency',
    align: 'right',
    finance: false,
    render: currencyCell,
  },
  {
    key: 'entry_cost_with_vat',
    label: 'Вход с НДС',
    block: 'assessment',
    width: 140,
    filterType: 'currency',
    align: 'right',
    finance: false,
    render: currencyCell,
  },

  // === ОБЯЗАТЕЛЬСТВА (6) ===
  {
    key: 'contract_amount_with_vat',
    label: 'Сумма договора с НДС',
    block: 'commitments',
    width: 150,
    filterType: 'currency',
    align: 'right',
    finance: false,
    render: currencyCell,
  },
  {
    key: 'contract_sign_date',
    label: 'Дата подписания',
    block: 'commitments',
    width: 110,
    filterType: 'date',
    align: 'left',
    finance: false,
    render: (value) => <span className="text-gray-600">{dateCell(value)}</span>,
  },
  {
    key: 'contract_close_date',
    label: 'Дата закрытия',
    block: 'commitments',
    width: 110,
    filterType: 'date',
    align: 'left',
    finance: false,
    render: (value) => <span className="text-gray-600">{dateCell(value)}</span>,
  },
  {
    key: 'vendor_payment_plan_date',
    label: 'Оплата вендору (план)',
    block: 'commitments',
    width: 110,
    filterType: 'date',
    align: 'left',
    finance: false,
    render: (value) => <span className="text-gray-600">{dateCell(value)}</span>,
  },
  {
    key: 'vendor_payment_fact_amount',
    label: 'Оплата вендору (факт)',
    block: 'commitments',
    width: 150,
    filterType: 'currency',
    align: 'right',
    finance: false,
    render: currencyCell,
  },
  {
    key: 'client_payment_fact_date',
    label: 'Оплата заказч. (факт)',
    block: 'commitments',
    width: 120,
    filterType: 'date',
    align: 'left',
    finance: false,
    render: (value) => <span className="text-gray-600">{dateCell(value)}</span>,
  },

  // === ЗАТРАТЫ (9, finance only) ===
  {
    key: 'expenses_equipment',
    label: 'Оборудование',
    block: 'expenses',
    width: 130,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'expenses_logistics',
    label: 'Логистика',
    block: 'expenses',
    width: 120,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'expenses_other',
    label: 'Прочие',
    block: 'expenses',
    width: 120,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'expenses_installation',
    label: 'Монтаж/услуги',
    block: 'expenses',
    width: 130,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'expenses_overtime',
    label: 'Переработки',
    block: 'expenses',
    width: 120,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'expenses_insurance',
    label: 'Страхование',
    block: 'expenses',
    width: 120,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'expenses_credit',
    label: 'Кредит',
    block: 'expenses',
    width: 110,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'expenses_partnership',
    label: '% партнерство',
    block: 'expenses',
    width: 120,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'expenses_penalty',
    label: 'Пеня',
    block: 'expenses',
    width: 100,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },

  // === СУБПОДРЯД И НАЛОГИ (3, finance only) ===
  {
    key: 'subcontract_percent',
    label: '% субподряда',
    block: 'subcontract',
    width: 100,
    filterType: 'percent',
    align: 'right',
    finance: true,
    render: percentCell,
  },
  {
    key: 'subcontract_amount',
    label: 'Сумма субподряда',
    block: 'subcontract',
    width: 130,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'taxes_amount',
    label: 'Налоги',
    block: 'subcontract',
    width: 120,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },

  // === ИТОГОВАЯ КАЛЬКУЛЯЦИЯ (4, finance only) ===
  {
    key: 'result_margin',
    label: 'Маржа, тг',
    block: 'final_calc',
    width: 130,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'result_margin_percent',
    label: 'Маржа, %',
    block: 'final_calc',
    width: 90,
    filterType: 'percent',
    align: 'right',
    finance: true,
    render: percentCell,
  },
  {
    key: 'result_taxes',
    label: 'Налоги итог',
    block: 'final_calc',
    width: 120,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
  {
    key: 'result_gross_profit',
    label: 'Валовая прибыль',
    block: 'final_calc',
    width: 140,
    filterType: 'currency',
    align: 'right',
    finance: true,
    render: currencyCell,
  },
];
